import {useMemo} from "react";

import {WorkspaceTour, type WorkspaceTourStep} from "./WorkspaceTour";

interface CashFlowDetailedTourProps {
    open: boolean;
    activeTab: string;
    onClose: () => void;
    onTabChange: (tab: string) => void;
}

const TOUR_STEPS: WorkspaceTourStep[] = [
    {
        id: "cashflow-panel",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-panel"]',
        title: "This is the standalone 13-week cash forecast",
        body: "This tab is kept separate from the monthly workbench. It is a weekly cash-management surface built from Xero actuals, invoice schedules, and direct weekly overrides.",
        badge: "13 Week Cash",
        placement: "bottom",
    },
    {
        id: "cashflow-controls",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-controls"]',
        title: "Use these controls to refresh the forecast and switch views",
        body: "Forecast is the live weekly cash model. Variance compares finished weeks against captured snapshots. Refresh pulls the latest source data and rebuilds the weekly projection.",
        badge: "Updates",
        placement: "bottom",
    },
    {
        id: "cashflow-assumptions",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-assumptions"]',
        title: "This panel explains how the weekly forecast is built",
        body: "Open this when you want the model logic in plain English: actuals treatment, AR and AP scheduling, weekly averages, tax timing, and what kinds of updates flow through on refresh.",
        badge: "Method",
        placement: "bottom",
    },
    {
        id: "cashflow-kpis",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-kpis"]',
        title: "These KPIs show the immediate cash position",
        body: "Current Cash, Runway, Avg Weekly Burn, and Min Cash Point give the quickest read on whether the weekly path is tightening or stabilising.",
        badge: "Readout",
        placement: "bottom",
    },
    {
        id: "cashflow-weekly-matrix",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-weekly-matrix"]',
        title: "Click forecast cells here to override a week directly",
        body: "This is the main update surface for 13-week cash. Actual columns are read-only; forecast cells can be edited inline, saved with a note, and cleared later to remove the override. Blue cells show manual overrides that are recorded in Ops and Revisions.",
        badge: "Updates",
        placement: "top",
    },
    {
        id: "cashflow-formula-tooltips",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-weekly-matrix"]',
        title: "Hover a cash-flow cell to see the formula basis",
        body: "Forecast cells show whether the value came from the 13-week average, scheduled AR/AP, BAS timing, repeating invoices, or a manual override. That gives a quick Excel-style trace without leaving the grid.",
        badge: "Trace",
        placement: "top",
    },
    {
        id: "cashflow-scenarios",
        tab: "cashflow_explorer",
        selector: '[data-tour="cashflow-scenarios"]',
        title: "These scenario cards give a quick stress read on ending cash and runway",
        body: "Use them as a fast frame for downside, base, and upside outcomes after the weekly model is rebuilt. They are summary views, not the main editing surface.",
        badge: "Scenarios",
        placement: "bottom",
    },
    {
        id: "cashflow-ops-tab",
        tab: "cashflow_explorer",
        selector: '[data-tour="dashboard-tab-trigger-ops"]',
        title: "Ops and Revisions is where committed weekly overrides can be undone",
        body: "Use this tab after the walkthrough to review active cash-flow ops, filter agent versus human changes, and undo committed weekly overrides.",
        badge: "Revisions",
        placement: "top",
    },
];

export function CashFlowDetailedTour({open, activeTab, onClose, onTabChange}: CashFlowDetailedTourProps) {
    const steps = useMemo(() => TOUR_STEPS, []);

    return (
        <WorkspaceTour
            open={open}
            activeTab={activeTab}
            onClose={onClose}
            onTabChange={onTabChange}
            steps={steps}
            dialogLabel="13 week cash flow walkthrough"
            returnTabOnClose="cashflow_explorer"
        />
    );
}
